// ─────────────────────────────────────────────────────────────────────────────
// Crew — PWA entry (Agent A; hash-routing for Agent D's internal surfaces).
//
// The SW (public/sw.js) handles Web Push display + notification clicks. We
// register it lazily after load so it never blocks first paint. Agent B builds
// the real app under <App/>; this is the minimal bootstrap.
//
// HASH ROUTING (Agent D): the metrics dashboard (#/metrics) and privacy / data-
// rights surface (#/privacy) are mounted here — NOT inside App.tsx — so they
// survive Agent B's full App.tsx rewrite and stay reachable without coupling to
// the consumer UI. Anything else falls through to <App/>.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import App from './App';
import MetricsDashboard from './MetricsDashboard';
import Privacy from './Privacy';
import './index.css';

function routeFor(hash: string): React.ReactElement {
  const route = hash.replace(/^#/, '');
  if (route === '/metrics') return <MetricsDashboard />;
  if (route === '/privacy') return <Privacy />;
  return <App />;
}

const rootEl = document.getElementById('root');
if (rootEl) {
  const root: Root = createRoot(rootEl);
  const render = () =>
    root.render(<React.StrictMode>{routeFor(window.location.hash)}</React.StrictMode>);
  render();
  // Re-render on hash change so #/metrics ↔ #/privacy ↔ app switches live.
  window.addEventListener('hashchange', render);
}

// Register the push/offline service worker (best-effort).
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* SW registration is non-fatal; push simply won't be available. */
    });
  });
}
